import React, { useEffect, useState, useRef } from 'react';
import Lottie from 'lottie-react';
import robotAnimation from '../animations/robot-bot.json';
import coreEngineAnimation from '../animations/core-engine.json';
import testRunningAnimation from '../animations/test-running.json';

const ANIMATION_INTERVAL = 3000;
const components = ['setup', 'core', 'test'];

export const FrameworkDiagram = ({ onComponentSelect, activeComponent }) => {
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Refs for controlling each animation
  const robotRef = useRef();
  const coreRef = useRef();
  const testRef = useRef();

  useEffect(() => {
    if (!isAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % components.length;
        return next;
      });
    }, ANIMATION_INTERVAL);

    return () => clearInterval(interval);
  }, [isAutoPlay]);

  useEffect(() => {
    if (isAutoPlay) {
      onComponentSelect(components[currentIndex]);
    }
  }, [currentIndex, isAutoPlay, onComponentSelect]);

  // Control animations based on active component
  useEffect(() => {
    if (robotRef.current && coreRef.current && testRef.current) {
      // Pause all animations first
      robotRef.current.pause();
      coreRef.current.pause();
      testRef.current.pause();
      
      // Play only the active one
      if (activeComponent === 'setup') {
        robotRef.current.play();
      } else if (activeComponent === 'core') {
        coreRef.current.play();
      } else if (activeComponent === 'test') {
        testRef.current.play();
      }
    }
  }, [activeComponent]);

  const handleComponentClick = (component, index) => {
    setIsAutoPlay(false);
    setCurrentIndex(index);
    onComponentSelect(component);
  };

  const isActive = (component) => activeComponent === component;

  return (
    <div className="framework-diagram-v2">
      <div className="diagram-container-v2">
        {/* Setup Phase - Robot Configuring */}
        <div
          className={`diagram-component-v2 setup-phase ${isActive('setup') ? 'active' : ''}`}
          onClick={() => handleComponentClick('setup', 0)}
        >
          <div className="component-icon-area">
            <div className="lottie-container">
              <Lottie
                animationData={robotAnimation}
                loop={true}
                autoplay={false}
                lottieRef={robotRef}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
          <div className="component-label">Setup Phase</div>
          <div className="component-sublabel">Configuring Environment</div>
        </div>

        {/* Connection Line with flowing particles */}
        <div className="connection-line left">
          <svg className="connection-svg" viewBox="0 0 100 4" preserveAspectRatio="none">
            <line x1="0" y1="2" x2="100" y2="2" className="connection-base" />
            {(isActive('setup') || isActive('core')) && (
              <>
                <circle r="2" className="flow-particle particle-flow-1">
                  <animateMotion dur="1.5s" repeatCount="indefinite" path="M0,2 L100,2" />
                </circle>
                <circle r="1.5" className="flow-particle particle-flow-2">
                  <animateMotion dur="1.5s" begin="0.5s" repeatCount="indefinite" path="M0,2 L100,2" />
                </circle>
              </>
            )}
          </svg>
        </div>

        {/* Core / Engine */}
        <div
          className={`diagram-component-v2 core-engine ${isActive('core') ? 'active' : ''}`}
          onClick={() => handleComponentClick('core', 1)}
        >
          <div className="component-icon-area">
            <div className="lottie-container">
              <Lottie
                animationData={coreEngineAnimation}
                loop={true}
                autoplay={false}
                lottieRef={coreRef}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
          <div className="component-label"><i>useNodeBoot()</i></div>
          <div className="component-sublabel">Testing Engine Hook</div>
        </div>

        {/* Connection Line with flowing particles */}
        <div className="connection-line right">
          <svg className="connection-svg" viewBox="0 0 100 4" preserveAspectRatio="none">
            <line x1="0" y1="2" x2="100" y2="2" className="connection-base" />
            {(isActive('core') || isActive('test')) && (
              <>
                <circle r="2" className="flow-particle particle-flow-1">
                  <animateMotion dur="1.5s" repeatCount="indefinite" path="M0,2 L100,2" />
                </circle>
                <circle r="1.5" className="flow-particle particle-flow-2">
                  <animateMotion dur="1.5s" begin="0.5s" repeatCount="indefinite" path="M0,2 L100,2" />
                </circle>
              </>
            )}
          </svg>
        </div>

        {/* Test Execution */}
        <div
          className={`diagram-component-v2 test-phase ${isActive('test') ? 'active' : ''}`}
          onClick={() => handleComponentClick('test', 2)}
        >
          <div className="component-icon-area">
            <div className="lottie-container">
              <Lottie
                animationData={testRunningAnimation}
                loop={true}
                autoplay={false}
                lottieRef={testRef}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
          <div className="component-label">Test Execution</div>
          <div className="component-sublabel">Running Test Suite</div>
        </div>
      </div>
    </div>
  );
};
